/**
 * Draggable zoom-lens ("Enable Magnifier" tool) shared by the legacy and
 * adaptive SHSAT players. A BARE floating lens (no chrome / title bar) that
 * shows a magnified (2x) copy of whatever test content is CURRENTLY visible on
 * screen — matching the official TestNav magnifier. The WHOLE lens is
 * draggable; it's turned off from the tools menu ("Disable Magnifier").
 *
 * Live view (incl. nested scroll): the lens holds a cloned copy of the target
 * content element (``#digitalSHSATModule__body``), given the target's OWN class
 * and its EXACT on-screen box (width x height) so the whole nested layout —
 * incl. the passage's own inner scroll container — resolves identically. We
 * then mirror ``scrollTop``/``scrollLeft`` of the target AND every scrollable
 * element in the cloned subtree from the live DOM, refreshing on ANY scroll via
 * a CAPTURING document scroll listener (scroll doesn't bubble, so capture is
 * required to catch the passage's inner scroll). So the magnified copy always
 * reflects the elements currently on screen, not the top-of-passage snapshot.
 *
 * Never scrolls/selects the passage: the lens captures pointer events over its
 * whole area (container pointer-events:auto, clone pointer-events:none), drag
 * mousedown preventDefault + user-select:none suppress text selection/auto-
 * scroll, and a non-passive wheel handler blocks wheel-over-lens from reaching
 * the passage.
 *
 * The lens is rendered OUTSIDE the target (a sibling) so cloning never recurses
 * into the lens. View-only static copy — no persistence.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const ZOOM = 2;
const LENS_W = 480;
const LENS_H = 320;

export function MagnifierLens(props: {
  active: boolean;
  /** id of the content element to magnify (default the SHSAT body). */
  targetId?: string;
}): JSX.Element | null {
  const { active, targetId = 'digitalSHSATModule__body' } = props;
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 120, y: 160 });
  const [html, setHtml] = useState<string>('');
  const lensRef = useRef<HTMLDivElement>(null);
  const cloneRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ dx: number; dy: number } | null>(null);
  const rafRef = useRef<number | null>(null);

  // Snapshot the target's HTML on open + whenever it mutates, so the
  // magnified copy reflects the current passage/question structure.
  useEffect(() => {
    if (!active) return;
    const target = document.getElementById(targetId);
    if (!target) return;
    const snapshot = () => setHtml(target.innerHTML);
    snapshot();
    const observer = new MutationObserver(snapshot);
    observer.observe(target, {
      childList: true, subtree: true, characterData: true, attributes: true,
    });
    return () => observer.disconnect();
  }, [active, targetId]);

  // Mirror scrollTop/scrollLeft of the target AND every element in the cloned
  // subtree from the live DOM, so nested scroll containers (the passage's own
  // inner scroll) show their CURRENTLY-visible content — not the load-time top.
  // The clone is built from the target's innerHTML, so its descendant list
  // corresponds 1:1 (document order) with the target's; copy by index.
  const syncScroll = useCallback(() => {
    const target = document.getElementById(targetId);
    const clone = cloneRef.current;
    if (!target || !clone) return;
    // Body-level scroll (the clone root == the body clone).
    if (clone.scrollTop !== target.scrollTop) clone.scrollTop = target.scrollTop;
    if (clone.scrollLeft !== target.scrollLeft) clone.scrollLeft = target.scrollLeft;
    // Nested scrollable descendants (passage inner scroll, etc.).
    const liveEls = target.querySelectorAll<HTMLElement>('*');
    const cloneEls = clone.querySelectorAll<HTMLElement>('*');
    const n = Math.min(liveEls.length, cloneEls.length);
    for (let i = 0; i < n; i += 1) {
      const live = liveEls[i];
      const cl = cloneEls[i];
      if (cl.scrollTop !== live.scrollTop) cl.scrollTop = live.scrollTop;
      if (cl.scrollLeft !== live.scrollLeft) cl.scrollLeft = live.scrollLeft;
    }
  }, [targetId]);

  // Position + scale the clone so the content under the lens is magnified in
  // place. The clone box exactly matches the target's on-screen rect (so the
  // nested layout + scroll resolve identically); scroll is handled by
  // syncScroll, not by a top offset.
  const reposition = useCallback(() => {
    const target = document.getElementById(targetId);
    const wrap = cloneRef.current;
    if (!target || !wrap) return;
    const b = target.getBoundingClientRect();
    wrap.style.left = `${b.left - pos.x}px`;
    wrap.style.top = `${b.top - pos.y}px`;
    wrap.style.width = `${b.width}px`;
    wrap.style.height = `${b.height}px`;
    const lensCx = pos.x + LENS_W / 2;
    const lensCy = pos.y + LENS_H / 2;
    wrap.style.transformOrigin = `${lensCx - b.left}px ${lensCy - b.top}px`;
    wrap.style.transform = `scale(${ZOOM})`;
  }, [pos.x, pos.y, targetId]);

  const update = useCallback(() => {
    reposition();
    syncScroll();
  }, [reposition, syncScroll]);

  useLayoutEffect(() => {
    if (active) update();
  }, [active, html, update]);

  // Keep the magnified view live: refresh on ANY scroll (capturing, so the
  // passage's nested scroll — which doesn't bubble — is caught) and on resize.
  // Coalesced through rAF so rapid scroll events don't thrash.
  useEffect(() => {
    if (!active) return;
    const onChange = () => {
      if (rafRef.current != null) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        update();
      });
    };
    document.addEventListener('scroll', onChange, true); // capture: catches nested scroll
    window.addEventListener('resize', onChange);
    return () => {
      document.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [active, update]);

  // Drag from anywhere on the lens. Suppress text selection on the page while
  // dragging so moving the lens never selects/auto-scrolls the passage.
  useEffect(() => {
    if (!active) return;
    // Pointer events cover mouse, touch (iPad) and pen with one path; the lens
    // sets touchAction:'none' so touch-dragging doesn't scroll the passage.
    const onMove = (e: PointerEvent) => {
      if (!dragState.current) return;
      e.preventDefault();
      const nx = Math.max(0, Math.min(e.clientX - dragState.current.dx, window.innerWidth - LENS_W));
      const ny = Math.max(0, Math.min(e.clientY - dragState.current.dy, window.innerHeight - LENS_H));
      setPos({ x: nx, y: ny });
    };
    const onUp = () => {
      if (!dragState.current) return;
      dragState.current = null;
      document.body.style.userSelect = '';
      (document.body.style as any).webkitUserSelect = '';
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      // Defensive: restore selection if unmounted mid-drag.
      document.body.style.userSelect = '';
      (document.body.style as any).webkitUserSelect = '';
    };
  }, [active]);

  // Block wheel-over-lens from scrolling the passage beneath. React's onWheel
  // is passive (can't preventDefault), so attach a non-passive native listener.
  useEffect(() => {
    if (!active) return;
    const el = lensRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => { e.preventDefault(); e.stopPropagation(); };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [active]);

  if (!active) return null;

  const startDrag = (e: React.PointerEvent) => {
    e.preventDefault(); // stop the browser starting a text selection
    dragState.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    document.body.style.userSelect = 'none';
    (document.body.style as any).webkitUserSelect = 'none';
  };

  return (
    <div
      ref={lensRef}
      className="digitalSHSATModule__magnifier"
      onPointerDown={startDrag}
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: LENS_W,
        height: LENS_H,
        overflow: 'hidden',
        border: '2px solid #676767',
        borderRadius: 6,
        boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
        background: '#ffffff',
        zIndex: 1500,
        cursor: 'move',
        pointerEvents: 'auto',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'none',
      }}
    >
      {/* Hide (magnified) scrollbars inside the clone so mirrored nested-scroll
          containers don't show scaled scrollbars in the lens. */}
      <style>{`
        .digitalSHSATModule__magnifier ::-webkit-scrollbar { width: 0 !important; height: 0 !important; display: none !important; }
        .digitalSHSATModule__magnifier * { scrollbar-width: none !important; }
      `}</style>
      {/* Magnified clone of the content beneath the lens. Given the target's
          own class + its exact box so its layout + nested scroll match 1:1;
          pointer-events off so the whole lens stays draggable and the clone
          never receives interaction (can't scroll/select the passage). */}
      <div
        ref={cloneRef}
        className="digitalSHSATModule__body"
        style={{
          position: 'absolute',
          overflow: 'hidden',
          pointerEvents: 'none',
          userSelect: 'none',
          transformOrigin: '0 0',
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

export default MagnifierLens;
